import type { FeatureMiddleware } from '@cast/api-client';

import {
  MOCK_CUSTOM_DASHBOARDS_CONTINUE_WAIT_RESPONSE,
  MOCK_CUSTOM_DASHBOARDS_QUERY_RESPONSE,
} from '../fixtures/custom-dashboards/load';
import { MOCK_CUSTOM_DASHBOARDS_SCHEMA } from '../fixtures/custom-dashboards/schema';
import {
  createMockAllocationGroupSummaryWithTpu,
  createMockAllocationGroupWorkloadWithTpu,
  createMockClusterCostItemWithTpu,
  createMockClusterSummaryWithTpu,
  createMockNamespaceCostSummaryItemWithTpu,
  createMockNamespaceReportWithTpu,
  createMockOrgClusterCostItemWithTpu,
  createMockOrgClustersSummaryWithTpu,
  createMockTpuCapabilities,
  createMockWorkloadCostItemWithTpu,
  generateMockArray,
} from '../fixtures/tpu-cost-data';
import {
  MOCK_CREATE_RESTRICTION_SILENCE_RESPONSE,
  MOCK_PREVIEW_RESTRICTION_SILENCE_RESPONSE,
  MOCK_RESTRICTION_SILENCES_RESPONSE,
} from '../fixtures/restriction-silences';
import { getOverride } from '../overrides';

let customDashboardsContinueWaitServed = false;

export const costReport: FeatureMiddleware<'costReport'> = {
  /**
   * Mock reporting capabilities - controls whether TPU data appears in UI
   */
  getReportCapabilities: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      return resolve(
        createMockTpuCapabilities({
          tpuDataAvailable: true,
          tpuDataReceived: true,
          gpuDataAvailable: true,
          gpuMetricsAvailable: false,
          networkDataAvailable: false,
        })
      );
    }
    return next();
  },

  /**
   * Mock organization clusters cost report - used in organization-level clusters table
   */
  getOrganizationClustersReport: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      // Generate mix of clusters with and without TPU (total: 5)
      const clustersWithTpu = generateMockArray(
        (index) =>
          createMockOrgClusterCostItemWithTpu({
            clusterId: `cluster-tpu-${index}`,
            clusterName: `tpu-cluster-${index}`,
          }),
        3,
        3
      );

      const clustersWithoutTpu = generateMockArray(
        (index) =>
          createMockOrgClusterCostItemWithTpu({
            hasTPU: false,
            clusterId: `cluster-no-tpu-${index}`,
            clusterName: `no-tpu-cluster-${index}`,
          }),
        2,
        2
      );

      const allClusters = [...clustersWithTpu, ...clustersWithoutTpu];

      return resolve({
        clusters: allClusters,
        summary: createMockOrgClustersSummaryWithTpu(),
        topClustersCost: [], // Optional: could add top clusters here
        totalDailyCost: [], // Optional: could add daily breakdown here
      });
    }
    return next();
  },

  /**
   * Mock cluster cost report - detailed time-series data with TPU breakdown
   */
  getCostReport: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      const items = generateMockArray(
        () => createMockClusterCostItemWithTpu(),
        5,
        5
      );

      return resolve({ items });
    }
    return next();
  },

  /**
   * Mock cluster summary - aggregated cluster metrics with TPU
   */
  getClusterSummary: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      return resolve({
        summary: createMockClusterSummaryWithTpu(),
      });
    }
    return next();
  },

  /**
   * Mock namespaces cost summary - used in namespace list table
   */
  getNamespacesCostSummary: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      const items = generateMockArray(
        (index) =>
          createMockNamespaceCostSummaryItemWithTpu({
            ...(index % 3 === 0 ? { hasTPU: false } : {}), // ~67% have TPU
            namespace: `namespace-${index}`,
          }),
        5,
        5
      );

      return resolve({
        items,
        count: String(items.length),
        nextCursor: '', // No pagination for mock data
      });
    }
    return next();
  },

  /**
   * Mock single namespace cost report
   */
  getNamespace: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      return resolve(createMockNamespaceReportWithTpu());
    }
    return next();
  },

  /**
   * Mock workloads cost report - used in workloads table
   */
  getWorkloadsCostReport: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      const items = generateMockArray(
        (index) =>
          createMockWorkloadCostItemWithTpu({
            ...(index % 2 !== 0 ? { hasTPU: false } : {}), // 50% have TPU
            workloadName: `workload-${index}`,
            namespace: `namespace-${index % 5}`,
          }),
        5,
        5
      );

      return resolve({ items });
    }
    return next();
  },

  /**
   * Mock workload cost summaries - paginated workload data
   */
  getWorkloadsCostSummaries: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      const items = generateMockArray(
        (index) =>
          createMockWorkloadCostItemWithTpu({
            ...(index % 2 !== 0 ? { hasTPU: false } : {}),
            workloadName: `workload-summary-${index}`,
            namespace: `namespace-${index % 5}`,
          }),
        5,
        5
      );

      return resolve({
        items,
        count: String(items.length),
        nextCursor: '', // No pagination for mock data
      });
    }
    return next();
  },

  /**
   * Mock single workload cost report
   */
  getSingleWorkloadCostReport: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      return resolve({
        item: createMockWorkloadCostItemWithTpu(),
      });
    }
    return next();
  },

  /**
   * Mock allocation group summaries - used in allocation groups table
   */
  getAllocationGroupSummaries: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      const items = generateMockArray(
        (index) =>
          createMockAllocationGroupSummaryWithTpu({
            ...(index % 3 === 0 ? { hasTPU: false } : {}), // ~67% have TPU
            groupId: `ag-${index}`,
            groupName: `allocation-group-${index}`,
          }),
        5,
        5
      );

      // Cast to any needed: OpenAPI types don't match the real API shape for allocation groups
      return resolve({ items: items });
    }
    return next();
  },

  /**
   * Mock allocation group workloads - used in allocation group drawer
   */
  getAllocationGroupWorkloads: async ({ next, resolve }) => {
    if (getOverride('mockTpuCostData')) {
      const items = generateMockArray(
        (index) =>
          createMockAllocationGroupWorkloadWithTpu({
            ...(index % 2 !== 0 ? { hasTPU: false } : {}),
            workloadName: `ag-workload-${index}`,
            namespace: `namespace-${index % 5}`,
          }),
        5,
        5
      );

      return resolve({
        items,
        count: String(items.length),
        nextCursor: '', // No pagination for mock data
      });
    }
    return next();
  },

  queryCustomDashboards: async ({ next, resolve }) => {
    if (getOverride('mockCustomDashboards')) {
      if (!customDashboardsContinueWaitServed) {
        customDashboardsContinueWaitServed = true;
        return resolve(MOCK_CUSTOM_DASHBOARDS_CONTINUE_WAIT_RESPONSE);
      }
      return resolve(MOCK_CUSTOM_DASHBOARDS_QUERY_RESPONSE);
    }
    return next();
  },

  getCustomDashboardsSchema: async ({ next, resolve }) => {
    if (getOverride('mockCustomDashboards')) {
      return resolve(MOCK_CUSTOM_DASHBOARDS_SCHEMA);
    }
    return next();
  },

  /**
   * Mock optimization-constraints silences — lets the suppressions drawer,
   * add drawer and preview be demoed without the reporting API
   */
  listRestrictionSilences: async ({ next, resolve }) => {
    if (getOverride('mockRestrictionSilences')) {
      return resolve(MOCK_RESTRICTION_SILENCES_RESPONSE);
    }
    return next();
  },

  createRestrictionSilence: async ({ next, resolve }) => {
    if (getOverride('mockRestrictionSilences')) {
      return resolve(MOCK_CREATE_RESTRICTION_SILENCE_RESPONSE);
    }
    return next();
  },

  deleteRestrictionSilence: async ({ next, resolve }) => {
    if (getOverride('mockRestrictionSilences')) {
      return resolve();
    }
    return next();
  },

  previewRestrictionSilence: async ({ next, resolve }) => {
    if (getOverride('mockRestrictionSilences')) {
      return resolve(MOCK_PREVIEW_RESTRICTION_SILENCE_RESPONSE);
    }
    return next();
  },
};
